/**
 * The camera, as arithmetic.
 *
 * Everything here is a pure function of numbers, which is the point: the framing maths used to
 * live inside `runCenter`/`runFit` in `features/map/mapController.ts`, where it could only be
 * exercised through a real stage, a real measured DOM and a real store. The camera ANIMATION
 * (V0.46) needs the same maths one step earlier — "where would this land?" — so that a tween
 * has a target to ease toward, and needs it to be testable without a browser, because
 * `client/` runs vitest in the `node` environment with no jsdom at all.
 *
 * So this module answers three questions and holds no state:
 *   - where should the camera BE to frame this box (`cameraFor`)
 *   - is that box already comfortably on screen (`boundsInside`)
 *   - where is a move PART WAY through (`frameAt`)
 *
 * The zoom bounds moved here with them. They are pure, `store/slices/camera.ts` re-exports
 * them so every existing import still resolves, and having `cameraFor` reach up into the store
 * for its own clamps would have inverted the layering this directory exists to keep.
 */

/** The camera: a world-space translation and a scale, applied as one CSS transform. */
export interface Camera {
  x: number;
  y: number;
  k: number;
}

/** A world-space box: `{a, b}` is its top-left corner, `{c, d}` its bottom-right. */
export interface Bounds {
  a: number;
  b: number;
  c: number;
  d: number;
}

export interface StageSize {
  width: number;
  height: number;
}

/** Zoom bounds, from the wheel and pinch handlers in infostation.html. */
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 2.2;
/** `fit()` will shrink to MIN_ZOOM but never magnify past this. */
export const FIT_MAX_ZOOM = 1.4;

export const clampZoom = (k: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k));

/**
 * How long a deliberate camera move takes, in milliseconds.
 *
 * Settled by trying 0-1200 in a prototype rather than by argument. Long enough to read as a
 * journey — which is the whole point, since a camera that teleports leaves you working out
 * where you went — and short enough that nobody waits for it.
 */
export const CAMERA_MS = 600;

/** The shortest a deliberate move is allowed to take, so a short hop still reads as a move. */
export const CAMERA_MIN_MS = 220;
/** Screen travel, in pixels, at or beyond which a move takes the full `CAMERA_MS`. */
export const CAMERA_FULL_TRAVEL = 480;

/**
 * How far a move is, in screen pixels — with a change of scale counted as travel too, since a
 * zoom moves everything on screen: half the scale range reads as a full trip.
 */
export function cameraTravel(from: Camera, to: Camera): number {
  return Math.hypot(to.x - from.x, to.y - from.y) + Math.abs(to.k - from.k) * (CAMERA_FULL_TRAVEL / 0.5);
}

/**
 * How long THIS move should take.
 *
 * `CAMERA_MS` was settled watching a long trip. Given to a short one it did not read as
 * deliberate but as lag: ease-out spends its second half almost still, so a node already near
 * the middle sat for most of a second with nothing visibly happening before its popup arrived.
 * The duration follows the distance instead — the full 600 for a full trip, down to a floor
 * that still reads as motion — and a move too small to see takes no time at all, which is
 * also what lets a caller sequence on `onEnd` without waiting out an animation nobody saw.
 */
export function cameraDuration(from: Camera, to: Camera, max: number = CAMERA_MS): number {
  const travel = cameraTravel(from, to);
  if (travel < 0.5) return 0;
  return Math.max(CAMERA_MIN_MS, Math.min(max, (max * travel) / CAMERA_FULL_TRAVEL));
}

/**
 * Ease-out cubic: fast away, gentle arrival.
 *
 * Out, not in-out: the move is a RESPONSE to something the user just did, so it should leave
 * immediately and spend its time landing, not build up first.
 */
export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/**
 * The camera part way between two of them.
 *
 * `done` is authoritative and the view it returns then is the target's EXACT floats — not
 * `0.99999 x` of it. `#world`'s inline transform string is the only camera oracle the
 * regression suite has (`e2e/helpers/app.ts` `getCamera` regexes it, and `settleCamera` waits
 * for it to stop changing byte for byte), so a tween that merely converged would leave every
 * camera assertion reading a number nobody wrote.
 */
export function frameAt(
  from: Camera,
  to: Camera,
  elapsed: number,
  duration: number = CAMERA_MS,
): { view: Camera; done: boolean } {
  if (!(duration > 0) || elapsed >= duration) return { view: { ...to }, done: true };
  const e = easeOutCubic(Math.max(0, elapsed) / duration);
  return {
    view: {
      x: from.x + (to.x - from.x) * e,
      y: from.y + (to.y - from.y) * e,
      k: from.k + (to.k - from.k) * e,
    },
    done: false,
  };
}

export interface FrameOptions {
  /** Clear space to leave on every side, in screen pixels. `fit()` uses 60, centring uses 120. */
  pad: number;
  /** How far this caller is willing to shrink. `fit()` goes to MIN_ZOOM; centring stops at 0.35. */
  minK: number;
  /** How far it is willing to magnify. Both callers stop at FIT_MAX_ZOOM. */
  maxK?: number;
  /**
   * May this move zoom IN? Expanding a branch says no: the group has just grown, so the
   * camera should pull back to take it in and never lunge at it. Requires `currentK`.
   */
  zoomIn?: boolean;
  currentK?: number;
}

/**
 * Where the camera has to be to frame `b` inside `stage`.
 *
 * Lifted verbatim out of `runFit`/`runCenter`, which differed only in `pad` and `minK` — the
 * divide guard is `runCenter`'s and cannot change `fit()`'s answer, because a span under one
 * pixel produces a scale far above `maxK` either way and both clamp to it.
 */
export function cameraFor(b: Bounds, stage: StageSize, o: FrameOptions): Camera {
  const { width: W, height: H } = stage;
  const maxK = o.maxK ?? FIT_MAX_ZOOM;
  let k = Math.min(
    maxK,
    Math.max(
      o.minK,
      Math.min((W - o.pad * 2) / Math.max(1, b.c - b.a), (H - o.pad * 2) / Math.max(1, b.d - b.b)),
    ),
  );
  // LAST, after every other clamp. Applied before `minK` it would do the opposite of what it
  // says: a floor of 0.35 over a camera sitting at 0.25 is a zoom IN.
  if (o.zoomIn === false && typeof o.currentK === 'number') k = Math.min(o.currentK, k);
  return {
    x: (W - (b.c - b.a) * k) / 2 - b.a * k,
    y: (H - (b.d - b.b) * k) / 2 - b.b * k,
    k,
  };
}

/**
 * How a caller wants the camera moved (V0.46).
 *
 * EVERY FIELD DEFAULTS TO THE BEHAVIOUR THAT WAS THERE BEFORE, which is the point of them
 * being options rather than a second pair of functions: boot's `fit()`, the router's deep
 * links, the chatbot's citations, the assignment highlight and `revealNodeOnMap` all keep the
 * instant, always-frame camera they have always had, and only the handful of gestures that
 * asked for motion get it.
 */
export interface CameraOpOptions {
  /** Ease there over `CAMERA_MS` instead of arriving. */
  animate?: boolean;
  /** `false` = pull back if the group does not fit, but never magnify. Expanding a branch. */
  zoomIn?: boolean;
  /** Skip the move entirely when the group is already comfortably on screen. */
  onlyIfOutside?: boolean;
  /** Ran once: the move settled (`true`), or never happened / was cut short (`false`). */
  onEnd?: (completed: boolean) => void;
}

/** Breathing room, in screen pixels, before a box counts as comfortably on screen. */
export const EDGE_MARGIN = 24;

/**
 * Is this box already on screen, with room to spare?
 *
 * The test that makes expanding a branch leave the camera alone when nothing was hidden. It
 * demands the WHOLE box on all four edges, like `isInsideStage` in the regression helpers and
 * for the same reason: a group half off the side is not visible in any sense the reader cares
 * about, however much of it happens to intersect the stage.
 */
export function boundsInside(
  b: Bounds,
  stage: StageSize,
  view: Camera,
  margin: number = EDGE_MARGIN,
): boolean {
  const left = b.a * view.k + view.x;
  const right = b.c * view.k + view.x;
  const top = b.b * view.k + view.y;
  const bottom = b.d * view.k + view.y;
  return (
    left >= margin &&
    top >= margin &&
    right <= stage.width - margin &&
    bottom <= stage.height - margin
  );
}
